import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import {
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Form2290Suspended = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Taxable and Suspended Vehicles in Form 2290"
          description="Discover the difference between taxable and suspended vehicles in Form 2290. Learn about filing requirements, exemptions, and how to report them accurately."
          keywords="Taxable vehicles, Suspended vehicles, Form 2290, Excise tax on vehicles, Agricultural Vehicles, Logging Vehicles, Truck Tax"
          canonical="resources/excise-tax/form-2290-taxable-suspended-vehicles/"
          ogType="article"
          jsonLdPricing="19.99"
          jsonLdAggregateRatingValue="4.97"
          jsonLdAggregateRatingCount="3830"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mt: 3,
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Taxable</span>
          </span>{' '}
          &{' '}
          <span className="highlight-container">
            <span className="highlight">Suspended</span>
          </span>{' '}
          Vehicles in Form 2290
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Save Money & Stay Compliant by e-filing your <b>Form 2290</b> with
          TaxZerone
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290taxable`}
          >
            E-file 2290 Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                {/* <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is IRS Form 2290?
                </Typography> */}
                <Stack
                  direction={{ xs: 'column-reverse', md: 'row' }}
                  spacing={{ xs: 1, sm: 2, md: 3 }}
                  sx={{ mb: 0 }}
                >
                  <Box>
                    <Box
                      itemScope
                      itemType="https://schema.org/Article"
                      sx={{
                        p: 3,
                        px: 3,
                        backgroundColor: '#f0f5f8',
                        borderLeft: '5px solid #01456c',
                        mb: 4,
                        borderRadius: 3,
                        position: 'relative',
                      }}
                    >
                      <link
                        itemProp="mainEntityOfPage"
                        href="https://www.taxzerone.com/resources/excise-tax/form-2290-taxable-suspended-vehicles/"
                      />

                      {/* Article requires an image */}
                      <div
                        itemProp="image"
                        itemScope
                        itemType="https://schema.org/ImageObject"
                        style={{ display: 'none' }}
                      >
                        <link
                          itemProp="url"
                          href="https://www.taxzerone.com/assets/images/logo.webp"
                        />
                        <data itemProp="width" value="380" />
                        <data itemProp="height" value="124" />
                      </div>

                      <Box
                        sx={{
                          display: 'grid',
                          justifyContent: 'flex-start',
                          alignItems: 'center',
                          textAlign: 'left',
                          mb: 2,
                        }}
                      >
                        <Typography variant="body2">
                          Last updated:{' '}
                          <time
                            itemProp="datePublished"
                            dateTime="2026-08-21T09:00:00-05:00"
                          >
                            August 21, 2026
                          </time>
                          <time
                            itemProp="dateModified"
                            dateTime="2026-08-21T09:00:00-05:00"
                            style={{ display: 'none' }}
                          >
                            August 21, 2026
                          </time>
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600 }}
                          itemProp="author"
                          itemScope
                          itemType="https://schema.org/Person"
                        >
                          -by{' '}
                          <span itemProp="name">
                            <a
                              itemProp="url"
                              href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                              style={{
                                color: 'inherit',
                                textDecoration: 'none',
                              }}
                            >
                              Alexia Zepeda
                            </a>
                          </span>{' '}
                          | TaxZerone
                        </Typography>
                      </Box>

                      <Typography
                        variant="h4"
                        itemProp="headline"
                        sx={{
                          fontWeight: 600,
                          mb: 2,
                          fontSize: {
                            md: 20,
                            sm: 18,
                            xs: 16,
                          },
                          color: '#000',
                        }}
                      >
                        Form 2290 Taxable vs. Suspended Vehicles at a Glance
                      </Typography>

                      <Box
                        component="ul"
                        itemProp="articleBody"
                        sx={{
                          mb: 0,
                          pl: 0,
                          listStyle: 'none',

                          '& li': {
                            mb: 1.6,
                            position: 'relative',
                            pl: 3,
                          },

                          '& li::before': {
                            content: '"★"',
                            position: 'absolute',
                            left: 0,
                            top: '-2px',
                            color: '#01456c',
                            fontSize: '18px',
                          },
                        }}
                      >
                        {/* Taxable Vehicles */}
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Taxable Vehicles:
                            </strong>{' '}
                            Heavy highway motor vehicles with a taxable gross
                            weight of{' '}
                            <strong style={{ fontWeight: 600 }}>
                              55,000 pounds or more
                            </strong>{' '}
                            that travel{' '}
                            <strong style={{ fontWeight: 600 }}>
                              more than 5,000 miles
                            </strong>{' '}
                            during the tax period{' '}
                            <strong style={{ fontWeight: 600 }}>
                              (7,500 miles for agricultural vehicles)
                            </strong>{' '}
                            are considered taxable and must file{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Form 2290
                            </strong>{' '}
                            with the IRS and pay the applicable Heavy Vehicle
                            Use Tax (HVUT).
                          </span>
                        </li>

                        {/* Suspended Vehicles */}
                        <li>
                          <span>
                            <strong style={{ fontWeight: 600 }}>
                              Suspended Vehicles:
                            </strong>{' '}
                            Heavy vehicles that travel{' '}
                            <strong style={{ fontWeight: 600 }}>
                              5,000 miles or less
                            </strong>{' '}
                            during the tax period{' '}
                            <strong style={{ fontWeight: 600 }}>
                              (7,500 miles or less for agricultural vehicles)
                            </strong>{' '}
                            are considered suspended vehicles. They are{' '}
                            <strong style={{ fontWeight: 600 }}>
                              exempt from paying HVUT
                            </strong>
                            , but{' '}
                            <strong style={{ fontWeight: 600 }}>
                              Form 2290 must still be filed
                            </strong>{' '}
                            to report their suspended status.
                          </span>
                        </li>
                      </Box>

                      <div
                        itemProp="publisher"
                        itemScope
                        itemType="https://schema.org/Organization"
                        style={{ display: 'none' }}
                      >
                        <span itemProp="name">TaxZerone</span>

                        <div
                          itemProp="logo"
                          itemScope
                          itemType="https://schema.org/ImageObject"
                        >
                          <link
                            itemProp="url"
                            href="https://www.taxzerone.com/assets/images/logo.webp"
                          />
                          <data itemProp="width" value="380" />
                          <data itemProp="height" value="124" />
                        </div>
                      </div>
                    </Box>
                    <Typography variant="body1" sx={{ mb: 2 }}>
                      When{' '}
                      <Link
                        href="/excise-tax/efile-irs-form-2290-heavy-vehicle-use-tax-online/"
                        sx={{ color: '#0202ff' }}
                      >
                        {' '}
                        filing Form 2290
                      </Link>
                      , truck owners must understand the difference between
                      taxable and suspended vehicles. They are two categories
                      used to differentiate certain types of vehicles for tax
                      purposes and have different implications for the taxes
                      owed.
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 0 }}>
                      Here, we will discuss taxable and suspended vehicles and
                      how to report them while filing Form 2290 with the IRS.
                    </Typography>
                  </Box>
                </Stack>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents</b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#form2290" sx={{ color: '#0202ff' }}>
                          What is Form 2290?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#taxable" sx={{ color: '#0202ff' }}>
                          Taxable Vehicles in Form 2290
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#suspended" sx={{ color: '#0202ff' }}>
                          Suspended Vehicles in Form 2290
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#duedate" sx={{ color: '#0202ff' }}>
                          Form 2290 Due Date
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="form2290"
                >
                  What is Form 2290?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  <b>Form 2290</b>, also known as the Heavy Highway Vehicle Use
                  Tax Return, is a tax form used to report and pay the Heavy
                  Vehicle Use Tax (HVUT) that applies to heavy vehicles
                  operating on public highways with a{' '}
                  <b>taxable gross weight of 55,000 pounds or more</b>.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The HVUT amount is determined based on the{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-taxable-gross-weight-and-weight-categories/"
                    sx={{ color: '#0202ff' }}
                  >
                    vehicle&apos;s gross weight
                  </Link>
                  .
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  However, vehicles that are used for less than 5,000 miles
                  (7,500 miles for agricultural vehicles) during the tax period
                  may be exempt from the HVUT taxes but required to file Form
                  2290.
                </Typography>
                <Typography variant="body1" sx={{ mb: 5 }}>
                  To know more about HVUT Form 2290 and how to file it, refer to{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-instructions/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 2290 Instructions
                  </Link>{' '}
                  for a step-by-step guide on completing your 2290 e-filing
                  process.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="taxable"
                >
                  Taxable Vehicles in Form 2290
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Taxable vehicles are those registered and used on public
                  highways for transporting goods and have a taxable gross
                  weight of 55,000 pounds or more. Vehicles like trucks,
                  semi-trucks, and truck tractors are considered highway motor
                  vehicles.
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  These vehicles are subject to the HVUT, and their owners must
                  file Form 2290 and pay the applicable tax to the IRS. The HVUT
                  amount is calculated based on the vehicle&apos;s gross weight.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The HVUT also applies to heavy vehicles leased or purchased by
                  a business.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  For example, if you bought a large truck that qualifies as a
                  taxable vehicle from the business dealer and drove it on any
                  public highway to your garage, the journey from the
                  seller&apos;s place to your destination would be considered
                  your first taxable use of the vehicle.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> If a truck is driven on highways for more than
                  5000 miles (7500 for agricultural trucks), it is taxable.
                  Vehicles that would be driven below this threshold are
                  considered{' '}
                  <Link
                    href="/excise-tax/free-e-file-form-2290-suspended-vehicles/"
                    sx={{ color: '#0202ff' }}
                  >
                    suspended vehicles
                  </Link>
                  .
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="suspended"
                >
                  Suspended Vehicles in Form 2290
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Suspended vehicles are also heavy vehicles used on public
                  highways but are exempt from paying the HVUT under specific
                  categories.
                </Typography>
                <Box
                  component="div"
                  sx={{ mb: 2, p: 2, borderRadius: 2 }}
                  className="gradient-bg"
                >
                  <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Box sx={{ width: '100%' }}>
                      <Typography
                        variant="body1"
                        sx={{ fontWeight: 600, mb: 1 }}
                      >
                        Enjoy Hassle-Free filing for 2290 Suspended Vehicles
                        with TaxZerone.
                      </Typography>
                      <Typography variant="body2">
                        If your vehicles meet the low-mileage suspension
                        requirements, filing Form 2290 (Suspended) with the IRS
                        is obligatory.{' '}
                      </Typography>
                    </Box>
                    <Box
                      sx={{ width: '100%', maxWidth: 150, textAlign: 'center' }}
                    >
                      <Button
                        variant="contained"
                        sx={{
                          textTransform: 'none',
                          boxShadow: 'none',
                          borderRadius: 2,
                          backgroundColor: '#fff',
                          color: '#333',
                          '&:hover, &:focus, &:active': {
                            color: '#fff',
                            background: '#172b6b',
                          },
                        }}
                        size="large"
                        href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290taxable`}
                      >
                        Click to E-file
                      </Button>
                    </Box>
                  </Stack>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  These vehicles, designated as category &ldquo;W&rdquo;, are
                  vehicles that have been used on public highways that do not
                  exceed 5,000 miles (7,500 miles for agricultural vehicles)
                  during the tax period. They are also commonly known as
                  low-mileage vehicles or taxable vehicles with low mileage.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  The suspended vehicles typically fall into one of the
                  following categories:
                </Typography>
                <ul>
                  <li>
                    <b>Agricultural Vehicles:</b> Trucks or tractors used
                    primarily for farming purposes and registered under the
                    agricultural category qualify for suspension of the HVUT. To
                    be eligible for this exemption, these vehicles must travel
                    fewer than 7,500 miles on public highways during the tax
                    period. However, the vehicle becomes taxable if the mileage
                    limit is exceeded, and the HVUT must be paid.
                  </li>
                  <li>
                    <b>Logging Vehicles:</b> Trucks that are registered with the
                    state and used exclusively for the transportation of
                    harvested forest products only qualify for the suspension of
                    HVUT. Similar to agricultural vehicles, logging vehicles
                    must travel fewer than 7,500 miles on public highways during
                    the tax period to maintain their suspended status.
                  </li>
                  <li>
                    <b>Government Vehicles:</b> Vehicles owned and operated by
                    federal, state, or local governments fall under the
                    suspended category and are exempt from the HVUT. This
                    includes vehicles used for public safety, emergency
                    response, and other governmental operations.
                  </li>
                  <li>
                    <b>Regular Vehicles:</b> In addition to the list above,
                    regular heavy vehicles that have been driven less than 5,000
                    miles in a tax period are also exempt from paying the HVUT
                    and are considered suspended.
                  </li>
                </ul>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Even though suspended vehicles are tax-exempt, owners of
                  suspended vehicles still need to file Form 2290 to indicate
                  that their vehicles are suspended from tax. Also, reporting
                  suspended vehicles in Form 2290 allows the IRS to keep track
                  of these vehicles and verify their exempt status.
                </Typography>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mb: 5, mt: 0, borderLeft: '3px solid rgb(102, 60, 0)' }}
                >
                  <b>Note:</b> While reporting taxable and suspended vehicles,
                  please enter the correct Vehicle Identification Number (VIN)
                  on your 2290 return, or you might need to file a{' '}
                  <Link
                    href="/excise-tax/efile-irs-form-2290-vin-correction-online/"
                    sx={{ color: '#0202ff' }}
                  >
                    VIN Correction
                  </Link>{' '}
                  for the 2290 return.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 87,
                  }}
                  id="duedate"
                >
                  Form 2290 Due Date
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  IRS{' '}
                  <Link
                    href="/resources/excise-tax/form-2290-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    Form 2290 Due Date
                  </Link>{' '}
                  will be the last day of the month following the vehicle&apos;s
                  First Used Month (FUM). The current tax6, and ends on June 30,
                  2027.
                </Typography>
                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    //  backgroundColor: '#f9f9f9',

                    border: '1px solid #ccc',
                    mb: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0 }}>
                    The IRS Form 2290 due date for Heavy Vehicle Use Taxes
                    (HVUT) for the tax year 2026 is
                    <br /> <b>August 31, 2027</b>.
                  </Typography>
                </Box>
                <Box
                  component="div"
                  sx={{
                    mb: 4,
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    backgroundSize: {
                      xs: '20% auto',
                      sm: '20% auto',
                      md: '20% auto',
                    },
                    border: '1px solid #ccc',
                  }}
                >
                  <Box sx={{ width: '100%' }}>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        mb: 1,
                        fontSize: '18px !important',
                      }}
                    >
                      Report your taxable and suspended vehicles on Form 2290
                      with TaxZerone
                    </Typography>
                    <Typography variant="body2">
                      E-file Form 2290 with TaxZerone and get your IRS-stamped
                      Schedule 1 in minutes.
                    </Typography>
                  </Box>
                  <Box sx={{ width: '100%', textAlign: 'left', mt: 2 }}>
                    <Button
                      variant="contained"
                      color="primary"
                      sx={{
                        textTransform: 'none',
                        boxShadow: 'none',
                        borderRadius: 2,
                      }}
                      size="large"
                      href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290taxable`}
                    >
                      Get Stamped Schedule 1
                    </Button>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>{' '}
      <Container maxWidth="lg" sx={{ mb: 8, mt: { xs: 6, sm: 6, md: 6 } }}>
        <Typography
          variant="h2"
          sx={{
            fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
            fontWeight: 600,
            mb: 3,
            textAlign: 'center',
          }}
        >
          Use TaxZerone to file Form 2290 for
          <br className="hide-xs" />
          Suspended Vehicles quickly!
        </Typography>
        <Grid
          container
          spacing={2}
          m={0}
          mt={0}
          className="MuiGrid-root-width"
          sx={{ flexDirection: { xs: 'column', md: 'row' } }}
        >
          <Grid
            size={{ md: 6, xs: 12 }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              textAlign: {
                lg: 'left',
                md: 'left',
                sm: 'center',
                xs: 'center',
              },
            }}
          >
            <Box sx={{ mb: { xs: 3, md: 0 }, mt: { xs: -3, md: 0 } }}>
              <Typography component="div" variant="body1" sx={{ mb: 3 }}>
                <ul
                  style={{
                    listStyleType: 'none',
                    paddingLeft: 0,
                    textAlign: 'left',
                  }}
                >
                  <li
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      <strong>Did you know?</strong> If your heavy vehicle
                      travels under 5,000 miles (7,500 for agricultural
                      vehicles), it&apos;s &quot;suspended&quot; from HVUT.
                    </Box>
                  </li>
                  <li
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 10,
                    }}
                  >
                    <CheckCircleRoundedIcon sx={{ color: '#1da94a', mr: 1 }} />{' '}
                    <Box>
                      E-File Form 2290 for suspended vehicles in 3 easy steps.
                    </Box>
                  </li>
                </ul>
              </Typography>

              <Box
                sx={{
                  width: 'auto',
                  textAlign: 'left',
                  alignItems: 'center',
                  display: 'inline-block',
                  mt: { xs: 2, sm: 2 },
                }}
              >
                <Button
                  variant="contained"
                  className="btn-primary medium"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=2290taxable`}
                  sx={{
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': {
                      transform: 'scale(1.1)',
                    },
                  }}
                >
                  Get Started Now!
                </Button>
                <Typography
                  variant="body2"
                  sx={{ mt: 1, mb: 0, textAlign: 'center' }}
                  id="auto-magic-2290"
                >
                  Receive your Schedule 1 in Minutes
                </Typography>
              </Box>
            </Box>
          </Grid>
          <Grid size={{ md: 6, xs: 12 }} textAlign="right">
            <Box
              component="div"
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <Box
                component="iframe"
                title="What is Form 2290: A Comprehensive Guide"
                src="https://www.youtube.com/embed/0LRvy1oHq1Q?&rel=0"
                sx={{
                  width: { xs: '100%', sm: 500 },
                  height: { xs: '100%', sm: 281 },
                  border: '10px solid #333 !important',
                  borderRadius: 5,
                }}
                allowFullScreen={true}
              />
            </Box>
          </Grid>
        </Grid>
      </Container>
    </AppLayout>
  );
};

export default Form2290Suspended;
